import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MainLayout from '../components/Layout/MainLayout';
import { useProjectContext } from '../contexts/ProjectContext';
import { useProject } from '../hooks/useProject';
import { useUser } from '../contexts/UserContext';
import styles from '../assets/styles/ProjectCreate.module.scss';

const ProjectCreate: React.FC = () => {
  const { user } = useUser();
  const { setSelectedProject, fetchProjects } = useProjectContext();
  const { projectName, setProjectName, handleProjectSubmit } = useProject(user?.tenant_id);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [projectQuota, setProjectQuota] = useState<{
    total_project_count: number;
    used_project_count: number;
  } | null>(null);

  useEffect(() => {
    const fetchProjectQuota = async () => {
      if (!user?.tenant_id) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/project-quota`, {
          credentials: 'include',
        });
        if (response.ok) {
          setProjectQuota(await response.json());
        }
      } catch (error) {
        console.error('Project quota fetch error:', error);
      }
    };
    void fetchProjectQuota();
  }, [user?.tenant_id]);

  const isProjectLimitReached = Boolean(
    projectQuota && projectQuota.used_project_count >= projectQuota.total_project_count,
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!projectName.trim() || isSubmitting || isProjectLimitReached) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await handleProjectSubmit(event);
      const createdProject = result?.data;

      if (result?.status !== 'success' || !createdProject?.id || !createdProject?.project_name) {
        setErrorMessage('プロジェクトの作成に失敗しました。');
        return;
      }

      await fetchProjects();
      setSelectedProject(createdProject.id, createdProject.project_name);
      navigate('/board', { replace: true });
    } catch (error) {
      console.error('Project creation error:', error);
      setErrorMessage('プロジェクトの作成に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout title="プロジェクト追加">
      <section className={styles.projectCreatePage}>
        <div className={styles.projectCreateCard}>
          <h2 className={styles.projectCreateTitle}>新しいプロジェクトを追加</h2>
          <p className={styles.projectCreateDescription}>
            広告を運用するプロジェクト名を入力してください。
          </p>

          {isProjectLimitReached && (
            <div className={styles.projectLimitMessage}>
              <p>プロジェクト作成上限に達しています。</p>
              <button type="button" onClick={() => navigate('/request-add-project')}>
                プロジェクト追加申請を行う
              </button>
            </div>
          )}

          <form className={styles.projectCreateForm} onSubmit={handleSubmit}>
            <label className={styles.projectNameLabel} htmlFor="project-name">
              プロジェクト名
            </label>
            <input
              id="project-name"
              className={styles.projectNameInput}
              type="text"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder="プロジェクト名を入力"
              required
              disabled={isSubmitting || isProjectLimitReached}
            />
            {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
            <div className={styles.projectCreateActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => navigate('/board')}
                disabled={isSubmitting}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={!projectName.trim() || isSubmitting || isProjectLimitReached}
              >
                {isSubmitting ? '作成中...' : '作成して初期設定へ'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </MainLayout>
  );
};

export default ProjectCreate;
